export const REVIEW_HOURS = 24;
const opts = (...xs) => xs.map(([id, label]) => ({ id, label }));
/** Trading-experience questionnaire, answered once after the account opens. Answers go to the CRM with the KYC. */
export const QUESTIONNAIRE = [
    {
        id: 'employment',
        title: 'What is your employment status?',
        options: opts(['employed', 'Employed'], ['self_employed', 'Self-employed'], ['retired', 'Retired'], ['student', 'Student'], ['unemployed', 'Not working']),
    },
    {
        id: 'income',
        title: 'What is your annual income?',
        hint: 'Before tax, in US dollars.',
        options: opts(['lt25k', 'Under $25,000'], ['25_50k', '$25,000 – $50,000'], ['50_100k', '$50,000 – $100,000'], ['100_250k', '$100,000 – $250,000'], ['gt250k', 'Over $250,000']),
    },
    {
        id: 'savings',
        title: 'How much do you have in savings and investments?',
        hint: 'Not counting your home.',
        options: opts(['lt10k', 'Under $10,000'], ['10_50k', '$10,000 – $50,000'], ['50_250k', '$50,000 – $250,000'], ['gt250k', 'Over $250,000']),
    },
    {
        id: 'source',
        title: 'Where will the money you trade with come from?',
        options: opts(['salary', 'Salary or wages'], ['savings', 'Savings'], ['business', 'Business income'], ['investments', 'Investments or pension'], ['inheritance', 'Inheritance or gift'], ['other', 'Other']),
    },
    {
        id: 'experience',
        title: 'How long have you traded leveraged products, forex or crypto?',
        options: opts(['none', 'Never'], ['lt1y', 'Less than a year'], ['1_3y', '1 to 3 years'], ['gt3y', 'More than 3 years']),
    },
    {
        id: 'frequency',
        title: 'How many trades did you make in the last 12 months?',
        options: opts(['0', 'None'], ['1_10', '1 – 10'], ['11_50', '11 – 50'], ['gt50', 'More than 50']),
    },
    {
        id: 'leverage',
        title: 'You open a $10,000 position with 1:10 leverage. The price falls 5%. What happens?',
        hint: 'This helps us check that leveraged trading suits you.',
        options: opts(['lose500', 'I lose $500, half of the $1,000 margin'], ['lose50', 'I lose $50'], ['nothing', 'Nothing until I close the position'], ['unsure', "I'm not sure"]),
    },
];
/** Returns the first problem with a set of answers, or null when every question has a valid answer. */
export function questionnaireProblem(a) {
    if (!a || typeof a !== 'object')
        return 'Answer every question to continue.';
    for (const q of QUESTIONNAIRE) {
        const v = a[q.id];
        if (typeof v !== 'string' || !q.options.some((o) => o.id === v))
            return `Answer every question to continue. Missing: ${q.title}`;
    }
    return null;
}
/** International dialling code by country (ISO 3166-1 alpha-2), so the phone prefix follows the country of residence. */
export const DIAL_CODES = Object.fromEntries(('AD376 AE971 AF93 AG1 AI1 AL355 AM374 AO244 AR54 AS1 AT43 AU61 AW297 AX358 AZ994 BA387 BB1 BD880 BE32 BF226 BG359 BH973 BI257 BJ229 BL590 ' +
    'BM1 BN673 BO591 BQ599 BR55 BS1 BT975 BW267 BY375 BZ501 CA1 CC61 CD243 CF236 CG242 CH41 CI225 CK682 CL56 CM237 CN86 CO57 CR506 CU53 CV238 ' +
    'CW599 CX61 CY357 CZ420 DE49 DJ253 DK45 DM1 DO1 DZ213 EC593 EE372 EG20 EH212 ER291 ES34 ET251 FI358 FJ679 FK500 FM691 FO298 FR33 GA241 GB44 ' +
    'GD1 GE995 GF594 GG44 GH233 GI350 GL299 GM220 GN224 GP590 GQ240 GR30 GT502 GU1 GW245 GY592 HK852 HN504 HR385 HT509 HU36 ID62 IE353 IL972 ' +
    'IM44 IN91 IO246 IQ964 IR98 IS354 IT39 JE44 JM1 JO962 JP81 KE254 KG996 KH855 KI686 KM269 KN1 KP850 KR82 KW965 KY1 KZ7 LA856 LB961 LC1 LI423 ' +
    'LK94 LR231 LS266 LT370 LU352 LV371 LY218 MA212 MC377 MD373 ME382 MF590 MG261 MH692 MK389 ML223 MM95 MN976 MO853 MP1 MQ596 MR222 MS1 MT356 ' +
    'MU230 MV960 MW265 MX52 MY60 MZ258 NA264 NC687 NE227 NF672 NG234 NI505 NL31 NO47 NP977 NR674 NU683 NZ64 OM968 PA507 PE51 PF689 PG675 PH63 ' +
    'PK92 PL48 PM508 PN64 PR1 PS970 PT351 PW680 PY595 QA974 RE262 RO40 RS381 RU7 RW250 SA966 SB677 SC248 SD249 SE46 SG65 SH290 SI386 SJ47 SK421 ' +
    'SL232 SM378 SN221 SO252 SR597 SS211 ST239 SV503 SX1 SY963 SZ268 TC1 TD235 TG228 TH66 TJ992 TK690 TL670 TM993 TN216 TO676 TR90 TT1 TV688 ' +
    'TW886 TZ255 UA380 UG256 UM1 US1 UY598 UZ998 VA39 VC1 VE58 VG1 VI1 VN84 VU678 WF681 WS685 XK383 YE967 YT262 ZA27 ZM260 ZW263')
    .split(' ')
    .map((x) => [x.slice(0, 2), `+${x.slice(2)}`]));
/** Flag emoji for a country code (shown as two letters where the system has no flag font). */
export const flagOf = (cc) => String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
//# sourceMappingURL=onboarding.js.map